import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface DeliveryStatus {
  deliveryId: string;
  status: string;
  courierName?: string;
  courierPhone?: string;
  courierVehicle?: {
    type?: string;
    make?: string;
    model?: string;
    color?: string;
  };
  pickupEta?: string;
  dropoffEta?: string;
  trackingUrl?: string;
  courierLocation?: {
    lat: number;
    lng: number;
  };
  isImminent?: boolean;
  lastUpdated?: string;
}

export const useDeliveryTracking = (deliveryId: string | null) => {
  const [deliveryStatus, setDeliveryStatus] = useState<DeliveryStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Fetch initial delivery status
  const fetchDeliveryStatus = useCallback(async () => {
    if (!deliveryId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Get the latest delivery information from our database (updated by webhooks)
      const { data, error: fetchError } = await supabase
        .from('deliveries')
        .select(`
          *,
          courier:courier,
          courier_location:courier_location,
          last_event:last_event
        `)
        .eq('delivery_id', deliveryId)
        .single();
      
      if (fetchError) {
        // If no delivery found, create a test delivery status
        if (fetchError.code === 'PGRST116') {
          setDeliveryStatus({
            deliveryId: deliveryId,
            status: 'pending',
            trackingUrl: `https://delivery.uber.com/orders/${deliveryId}`,
            lastUpdated: new Date().toISOString()
          });
          return;
        }
        throw fetchError;
      }
      
      if (data) {
        const courierData = data.courier || {};
        const locationData = data.courier_location || {};
        
        setDeliveryStatus({
          deliveryId: data.delivery_id,
          status: data.status,
          courierName: courierData.name,
          courierPhone: courierData.phone_number,
          courierVehicle: {
            type: courierData.vehicle_type,
            make: courierData.vehicle_make,
            model: courierData.vehicle_model,
            color: courierData.vehicle_color,
          },
          pickupEta: data.last_event?.data?.pickup_eta,
          dropoffEta: data.last_event?.data?.dropoff_eta,
          trackingUrl: data.tracking_url,
          courierLocation: locationData.lat && locationData.lng ? {
            lat: locationData.lat,
            lng: locationData.lng
          } : undefined,
          isImminent: data.last_event?.data?.courier_imminent,
          lastUpdated: data.updated_at,
        });
      }
    } catch (err) {
      console.error('Error fetching delivery status:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch delivery status'));
    } finally {
      setLoading(false);
    }
  }, [deliveryId]);

  // Set up real-time subscription for delivery updates
  useEffect(() => {
    if (!deliveryId) return;
    
    fetchDeliveryStatus();
    
    // Subscribe to changes on the deliveries table for this specific delivery
    const deliverySubscription = supabase
      .channel(`delivery-${deliveryId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'deliveries',
          filter: `delivery_id=eq.${deliveryId}`,
        },
        (payload) => {
          console.log('Delivery update received:', payload);
          fetchDeliveryStatus();
        }
      )
      .subscribe();
    
    return () => {
      deliverySubscription.unsubscribe();
    };
  }, [deliveryId, fetchDeliveryStatus]);
  
  return {
    deliveryStatus,
    loading,
    error,
    refreshStatus: fetchDeliveryStatus,
  };
}; 